import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import MediaPicker from '../../components/admin/MediaPicker';
import AdminModal from '../../components/admin/AdminModal';

const COLLAPSIBLE_MONTHS = ['TBC', 'Special Events'];
const REGULAR_MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const MONTH_ORDER = [...REGULAR_MONTHS, ...COLLAPSIBLE_MONTHS];

const emptyEntry = {
  year: '2026',
  month: 'January',
  title: '',
  poster: '',
  streamed: false,
  sortOrder: 0,
};

const normalizeYear = (year) => String(year);

function groupByYearMonth(entries) {
  const byYear = {};
  for (const entry of entries) {
    const year = normalizeYear(entry.year);
    if (!byYear[year]) byYear[year] = {};
    if (!byYear[year][entry.month]) byYear[year][entry.month] = [];
    byYear[year][entry.month].push(entry);
  }
  for (const year of Object.keys(byYear)) {
    for (const month of Object.keys(byYear[year])) {
      byYear[year][month].sort((a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title));
    }
  }
  return byYear;
}

export default function AdminSchedule() {
  const [entries, setEntries] = useState([]);
  const [form, setForm] = useState(emptyEntry);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [selectedYear, setSelectedYear] = useState('2026');
  const [formOpen, setFormOpen] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(() => apiGet('/schedule').then((data) => {
    setEntries(data);
    const counts = data.reduce((acc, entry) => {
      const year = normalizeYear(entry.year);
      acc[year] = (acc[year] || 0) + 1;
      return acc;
    }, {});
    const available = Object.keys(counts).sort();
    if (available.length && !counts[selectedYear]) {
      setSelectedYear(available.includes('2026') ? '2026' : available[available.length - 1]);
    }
    return data;
  }), [selectedYear]);

  useEffect(() => {
    load().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => groupByYearMonth(entries), [entries]);

  const years = useMemo(() => {
    const fromData = Object.keys(grouped);
    if (fromData.length === 0) return ['2026', '2027'];
    return fromData.sort();
  }, [grouped]);

  const formYears = useMemo(() => {
    const next = String(Math.max(new Date().getFullYear(), ...years.map(Number)) + 1);
    return [...new Set([...years, String(new Date().getFullYear()), next])].sort();
  }, [years]);

  const yearStats = useMemo(() => {
    const yearEntries = entries.filter((e) => normalizeYear(e.year) === selectedYear);
    const streamed = yearEntries.filter((e) => e.streamed).length;
    return { total: yearEntries.length, streamed, planned: yearEntries.length - streamed };
  }, [entries, selectedYear]);

  const monthsForYear = useMemo(() => {
    const monthMap = grouped[selectedYear] || {};
    const extraWithGames = COLLAPSIBLE_MONTHS.filter((month) => monthMap[month]?.length);
    return { monthMap, extraWithGames };
  }, [grouped, selectedYear]);

  const resetForm = () => {
    setForm(emptyEntry);
    setEditingId(null);
  };

  const closeForm = () => {
    setFormOpen(false);
    setFormError('');
    resetForm();
  };

  const toggleStreamed = async (entry) => {
    const next = { ...entry, streamed: !entry.streamed };
    setEntries((list) => list.map((e) => (e.id === entry.id ? next : e)));
    try {
      await apiPut(`/schedule/${entry.id}`, next);
    } catch (err) {
      setMessage(err.message);
      load();
    }
  };

  const handleSave = async () => {
    setMessage('');
    setFormError('');
    try {
      if (editingId) {
        await apiPut(`/schedule/${editingId}`, form);
        setMessage('Schedule entry updated.');
      } else {
        await apiPost('/schedule', form);
        setMessage('Schedule entry created.');
      }
      closeForm();
      setSelectedYear(normalizeYear(form.year));
      load();
    } catch (err) {
      setFormError(err.message);
    }
  };

  const startEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      year: entry.year,
      month: entry.month,
      title: entry.title,
      poster: entry.poster,
      streamed: entry.streamed,
      sortOrder: entry.sortOrder,
    });
    setFormError('');
    setFormOpen(true);
  };

  const startAddForMonth = (month) => {
    resetForm();
    setForm({ ...emptyEntry, year: selectedYear, month });
    setFormError('');
    setFormOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    await apiDelete(`/schedule/${id}`);
    if (editingId === id) closeForm();
    load();
  };

  const renderMonthBlock = (month) => {
    const games = monthsForYear.monthMap[month] || [];
    return (
      <div key={month} className="admin-schedule-month-block">
        <div className="admin-schedule-month-label">
          <span className="admin-schedule-month-name">{month}</span>
          <button
            type="button"
            className="admin-schedule-month-add"
            onClick={() => startAddForMonth(month)}
            title={`Add to ${month}`}
          >
            <i className="fas fa-plus" aria-hidden="true" />
          </button>
        </div>
        {games.length > 0 ? (
          <ul className="admin-schedule-entry-list">
            {games.map((entry) => (
              <li
                key={entry.id}
                className={`admin-schedule-entry ${entry.streamed ? 'is-streamed' : ''} ${editingId === entry.id ? 'is-editing' : ''}`}
              >
                {entry.poster ? (
                  <img src={entry.poster} alt="" className="admin-schedule-entry-thumb" />
                ) : (
                  <span className="admin-schedule-entry-thumb is-empty" aria-hidden="true">
                    <i className="fas fa-gamepad" />
                  </span>
                )}
                <span className="admin-schedule-entry-title">{entry.title}</span>
                <div className="admin-schedule-entry-actions">
                  <button
                    type="button"
                    className={`admin-schedule-icon-btn ${entry.streamed ? 'is-on' : ''}`}
                    onClick={() => toggleStreamed(entry)}
                    title={entry.streamed ? 'Streamed (click to mark planned)' : 'Mark as streamed'}
                  >
                    <i className="fab fa-twitch" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="admin-schedule-icon-btn"
                    onClick={() => startEdit(entry)}
                    title="Edit"
                  >
                    <i className="fas fa-pen" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="admin-schedule-icon-btn danger"
                    onClick={() => handleDelete(entry.id)}
                    title="Delete"
                  >
                    <i className="fas fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="admin-schedule-month-empty">—</p>
        )}
      </div>
    );
  };

  const yearHasEntries = entries.some((e) => normalizeYear(e.year) === selectedYear);

  return (
    <div className="admin-schedule-page">
      <div className="admin-notice-banner">
        <p>
          Edit schedule page copy (headings, intro, footnotes) in{' '}
          <Link to="/admin/stream-pages">Stream Pages → Schedule</Link>.
          This screen only manages game calendar entries.
        </p>
      </div>
      <div className="admin-schedule-toolbar">
        <div className="admin-schedule-toolbar-row">
          <div className="admin-schedule-year-group">
            {formYears.map((year) => (
              <button
                key={year}
                type="button"
                className={`admin-schedule-year-tab ${selectedYear === year ? 'active' : ''}`}
                onClick={() => setSelectedYear(year)}
              >
                {year}
              </button>
            ))}
          </div>

          <div className="admin-schedule-stats">
            <div className="admin-schedule-stat-chip">
              <span className="admin-schedule-stat-value">{yearStats.total}</span>
              <span className="admin-schedule-stat-label">Total</span>
            </div>
            <div className="admin-schedule-stat-chip admin-schedule-stat-chip--planned">
              <span className="admin-schedule-stat-value">{yearStats.planned}</span>
              <span className="admin-schedule-stat-label">Planned</span>
            </div>
            <div className="admin-schedule-stat-chip admin-schedule-stat-chip--streamed">
              <span className="admin-schedule-stat-value">{yearStats.streamed}</span>
              <span className="admin-schedule-stat-label">Streamed</span>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-primary admin-schedule-form-toggle"
            onClick={() => startAddForMonth(REGULAR_MONTHS[new Date().getMonth()])}
          >
            <i className="fas fa-plus" aria-hidden="true" />
            Add game
          </button>
        </div>
      </div>

      <AdminModal
        open={formOpen}
        as="form"
        onSubmit={handleSave}
        onClose={closeForm}
        size="md"
        title={editingId ? `Edit · ${form.title}` : `Add game to ${form.month} ${form.year}`}
        footer={(
          <>
            {formError && <span className="admin-modal__error">{formError}</span>}
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={() => handleDelete(editingId)}>
                <i className="fas fa-trash" aria-hidden="true" /> Delete
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={closeForm}>Cancel</button>
            <button type="submit" className="btn btn-primary">{editingId ? 'Save' : 'Add to schedule'}</button>
          </>
        )}
      >
        <div className="admin-modal-split admin-modal-split--poster">
          <div className="admin-modal-fields">
            <label className="admin-field">
              <span>Game title</span>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required autoFocus placeholder="Game title" />
            </label>
            <div className="admin-field-row">
              <label className="admin-field">
                <span>Year</span>
                <select value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })}>
                  {formYears.map((year) => <option key={year} value={year}>{year}</option>)}
                </select>
              </label>
              <label className="admin-field">
                <span>Month</span>
                <select value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })}>
                  {MONTH_ORDER.map((month) => <option key={month} value={month}>{month}</option>)}
                </select>
              </label>
            </div>
            <MediaPicker label="Poster" value={form.poster} onChange={(url) => setForm({ ...form, poster: url })} placeholder="https://..." />
            <label className="admin-switch">
              <input type="checkbox" checked={form.streamed} onChange={(e) => setForm({ ...form, streamed: e.target.checked })} />
              <span>Already streamed</span>
            </label>
          </div>
          <div className="admin-schedule-poster-preview">
            {form.poster ? <img src={form.poster} alt="" /> : <i className="fas fa-gamepad" aria-hidden="true" />}
          </div>
        </div>
      </AdminModal>

      {message && <p className="admin-message">{message}</p>}

      <div className="admin-schedule-calendar">
        {!yearHasEntries ? (
          <div className="admin-schedule-empty">
            <i className="fas fa-calendar-day" aria-hidden="true" />
            <p>No games scheduled for {selectedYear} yet.</p>
            {years.some((year) => year !== selectedYear) && (
              <p className="admin-schedule-empty-hint">
                Use the year tabs above to view another year&apos;s schedule.
              </p>
            )}
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => startAddForMonth('January')}
            >
              Add first entry
            </button>
          </div>
        ) : (
          <>
            <div className="admin-schedule-month-grid">
              {REGULAR_MONTHS.map(renderMonthBlock)}
            </div>
            {monthsForYear.extraWithGames.length > 0 && (
              <div className="admin-schedule-extra-section">
                {monthsForYear.extraWithGames.map(renderMonthBlock)}
              </div>
            )}
          </>
        )}

      </div>
    </div>
  );
}
